import styled, { css } from 'styled-components';

export const colors = {
  brand: '#E71037',
  brandLight: '#FF3A5E',
  bg: '#F2F6FB',
  bgChat: '#F2F6FB',
  text: '#1A1A1A',
  subtext: '#6B7280',
  divider: '#EEF2F6',
  chip: '#F6F8FB',
  chipBorder: '#E7ECF4',
  activeItem: '#EAF2FF',
  userBubble: '#ffffff',
  teoBubble: '#F3D6DE',
};

export const Page = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  height: 100vh;
  background: ${colors.bg};
`;

export const Sidebar = styled.aside`
  position: relative;
  background: #fff;
  padding: 16px 16px 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.06);
  overflow: visible;
  height: 100vh;
`;

export const Avatar = styled.img`
  grid-row: 1 / span 2;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
`;

export const HeaderActions = styled.div`
  display: flex;
  margin-top: 120px;
`;

export const IconButton = styled.button`
  border: none;
  background: ${colors.brand};
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

export const SidebarSection = styled.div`
  position: relative;
  padding: 20px 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${colors.divider};
  margin-top: 80px;

  &.recent {
    margin-top: 32px;
    padding-top: 24px;
    top: 40px;
  }
`;

export const SectionTitle = styled.span`
  font-weight: 600;
  color: ${colors.subtext};
`;

export const SectionSubtitle = styled.span`
  font-weight: 600;
  color: ${colors.subtext};
  opacity: 0.9;
`;

export const ClearAll = styled.button`
  border: none;
  background: transparent;
  color: ${colors.brand};
  font-weight: 700;
  cursor: pointer;
`;

export const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 6px 0 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ListItem = styled.li<{ $active?: boolean; $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 8px 12px 8px 12px;
  border-radius: 16px;
  background: ${({ $active }) => ($active ? colors.activeItem : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: background 0.25s ease;

  &:hover {
    background: ${({ $active }) => ($active ? colors.activeItem : '#F8FAFE')};
  }
`;

export const ItemLeft = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const ChatIcon = styled.span<{ $active?: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid ${({ $active }) => ($active ? colors.brand : '#9AA3AF')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  display: inline-block;
`;

export const ItemTitle = styled.span<{ $disabled?: boolean }>`
  flex: 1;
  min-width: 0;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  font-weight: 600;
  color: ${({ $disabled }) => ($disabled ? '#9ca3af' : colors.text)};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

export const EditInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-weight: 600;
  color: ${colors.text};
  width: 180px;
`;

export const ItemActions = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
`;

export const RoundIcon = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: none;
  background: ${colors.brand};
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
`;

export const ActivePillWrapper = styled.div`
  position: absolute;
  right: -55px;
  top: 0;
  bottom: 0;
  width: 160px;
  pointer-events: none;
  z-index: 999;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: visible;

  .big-halo {
    position: absolute;
    right: 10px;
    width: 60px;
    height: 60px;
    background: #f2f6fb;
    border-radius: 50%;

    display: flex;
    align-items: center;
    justify-content: center;

    opacity: 0;

    transition: top 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease;
  }

  .big-halo::after {
    content: '';
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #e50043;

    box-shadow: 0 8px 24px rgba(229, 0, 67, 0.55);

    display: block;
  }

  .pill {
    position: absolute;
    right: 50px;
    width: 0;
    height: 48px;

    background: #e50043;
    border-top-left-radius: 28px;
    border-bottom-left-radius: 28px;

    clip-path: ellipse(55% 70% at 100% 50%);
    opacity: 0;

    transition: width 280ms cubic-bezier(0.25, 0.9, 0.25, 1), opacity 180ms ease;
  }

  ${ListItem}:hover & .pill {
    width: 95px;
    opacity: 1;
  }

  ${ListItem}:hover & .big-halo {
    opacity: 1;
  }
`;

export const SidebarFooter = styled.div`
  bottom: 32px;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: column;
  gap: 12px;

  padding: 5px;
  margin-top: auto; 
`;

export const SettingsButton = styled.button`
  border: none;
  background: #f7f9fc;
  padding: 12px 16px;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  font-weight: 600;
`;

export const UserMini = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid ${colors.divider};
  padding: 10px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const UserAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  object-fit: cover;
`;

export const Fab = styled.button`
  position: absolute;
  right: 10px;

  width: 40px;
  height: 40px;

  border: none;
  background: ${colors.brand};
  border-radius: 999px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  box-shadow: 0 8px 24px rgba(231, 16, 55, 0.35);

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    pointer-events: none;
  }
`;

export const ChatPanel = styled.main`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;          
  background: ${colors.bgChat};
  border-radius: 24px;
`;

export const Window = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
`;

export const Messages = styled.div`
  flex: 1;                
  overflow-y: auto;       
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const MessagesWrapper = styled.div`
  flex: 1;            
  overflow-y: auto;   
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const SuggestionsBar = styled.div`
  padding: 8px 16px;

`;

export const SuggestionsTitle = styled.div`
  color: ${colors.subtext};
  font-weight: 600;
  margin-bottom: 8px;
`;

export const SuggestionsList = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 0;
`;

export const CarouselWrapper = styled.div`
  width: 80%;
  max-width: 900px;
  margin: 0 auto;

  overflow-x: auto;
  overflow-y: hidden;

  padding: 5px 0;

  white-space: nowrap;

  cursor: grab;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

export const Suggestion = styled.button`
  background: ${colors.chip};
  border: 1px solid ${colors.chipBorder};
  border-radius: 12px;
  padding: 12px 16px;

  max-width: 720px;
  width: 100%;

  text-align: center;
  white-space: normal;
  word-break: break-word;

  cursor: pointer;
  font-weight: 600;
  line-height: 20px;
`;

export const InputBar = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #fff;
  border-radius: 999px;
  margin: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

export const InputLeftIcon = styled.img`
  border-radius: 999px;
  background: ${colors.chip};
  position: absolute;
  left: 18px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: ${colors.chip};
`;

export const InputEl = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 16px;
  padding: 10px 8px;

  &::placeholder {
    color: #9aa3af;
  }
`;

export const SendBtn = styled.button`
  position: absolute;
  right: 18px;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: none;
  background: ${colors.brand};
  color: #fff;
  display: grid;
  place-items: center;
  box-shadow: 0 8px 24px rgba(231, 16, 55, 0.35);
  transition: opacity 0.2s ease, transform 0.15s ease;

  cursor: pointer;

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none; // 🔑 impede clique de vez
    box-shadow: none;
  }
`;


export const ContainerChat = styled.div`
  position: fixed;
  bottom: 120px;
  right: 40px;
  cursor: pointer;
  z-index: 999998;
`;

export const Container = styled.div`
  position: fixed;
  bottom: 120px;
  right: 40px;
  cursor: pointer;
  z-index: 999998;
  touch-action: none;
  user-select: none;

  @media (max-width: 480px) {
    right: 16px;
    bottom: 24px;
  }
`;

export const Bubble = styled.div`
  position: relative;
  cursor: pointer;
  width: 100px;
  height: 100px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-radius: 55px;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.03);
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
  }
`;

export const MessageBox = styled.div`
  position: absolute;
  right: 100px;
  width: 300px;
  background: #e50043;
  color: #fff;
  padding: 15px 22px;
  border-radius: 40px 0 40px 40px;

  display: flex;
  flex-direction: column;
  gap: 2px;

  white-space: nowrap;
  font-size: 15px;
  line-height: 18px;
  z-index: 2;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.2s ease forwards;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 480px) {
    right: 88px;
    width: calc(100vw - 120px);
    padding: 12px 16px;
    font-size: 13px;
    white-space: normal;
  }
`;

export const TeoImage = styled.img`
  width: 110px;
  height: 110px;
  padding: 8px;
  border-radius: 50%;
  object-fit: cover;
  object-fit: contain;
  object-position: center;
  cursor: pointer;

  z-index: 3;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  background: #e50043;
`;

export const TeoCircle = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  background: #e50043;
  cursor: pointer;

  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  z-index: 3;

  & > div {
    width: 130%;
    height: 130%;
    background: transparent;
    cursor: pointer !important;
  }
  pointer-events: auto;

  & * {
    pointer-events: none !important;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
  }
`;

export const ChatWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 120vh;
  background: #ffffff;
  z-index: 9999999;

  display: flex;
  align-items: center;
  justify-content: center;

  pointer-events: auto;
`;

export const ChatContent = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #fff;
`;

export const UserInitials = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: ${colors.brand};
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-size: 12px;
`;

export const SidebarHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 10px 0 0px;
  align-items: center;
  width: 100%;

  .brand-row {
    display: flex;
    align-items: center;
    gap: 18px;
  }
`;

export const Teologo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 999px;
  object-fit: cover;
`;

export const Brand = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: #e50043;
  letter-spacing: 6px;
  margin: 0;
`;

export const NewChatButton = styled.button`
  width: 90%;
  background: #e50043;
  border: none;
  padding: 8px 0;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  cursor: pointer;
  color: white;
  font-size: 22px;
  font-weight: 600;
  box-shadow: 0 14px 26px rgba(229, 0, 67, 0.28);

  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.02);
  }

  .icon {
    font-size: 32px;
    line-height: 0;
  }
`;

export const TypingBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  background: ${({ $role }) => ($role === 'user' ? colors.userBubble : '#fff')};
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  padding: 10px 16px;
  border-radius: 18px;
  margin-left: ${({ $role }) => ($role === 'assistant' ? '24px' : '0')};
  margin-bottom: 16px;

  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  span {
    width: 6px;
    height: 6px;
    background: ${colors.brand};
    border-radius: 50%;
    opacity: 0.4;
    animation: blink 1s infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes blink {
    0% { opacity: 0.3; transform: translateY(0); }
    50% { opacity: 1; transform: translateY(-2px); }
    100% { opacity: 0.3; transform: translateY(0); }
  }
`;

export const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 620px;
  width: fit-content;
  padding: 12px 18px;
  border-radius: 16px;

  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};

  background: ${({ $role }) =>
    $role === 'user' ? colors.userBubble : '#FDECEF'};

  color: ${({ $role }) =>
    $role === 'user' ? colors.text : '#4A0F1F'};

  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  white-space: pre-wrap;   /* respeita \n */
  line-height: 1.6;
  word-break: break-word;
    p {
    margin: 0 0 8px;
  }
`;

export const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
`;

export const TypingWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-left: 24px;
  margin-bottom: 16px;
`;

export const SkeletonBubble = styled.div`
  height: 48px;
  width: 70%;
  background: #e5e7eb;
  border-radius: 12px;
  margin-bottom: 12px;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% { opacity: .6 }
    50% { opacity: 1 }
    100% { opacity: .6 }
  }
`;

export const StatusDot = styled.span<{ status: 'open' | 'RESOLVED' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;

  background-color: ${({ status }) =>
    status === 'open' ? '#22c55e' : '#9ca3af'};
`;

export const ClosedBadge = styled.span`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
`;


export const OpenBadge = styled.span`
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-weight: 600;
  white-space: nowrap;
`;